import React, { useEffect, useState } from 'react';
import config from '../config';
import { Alert, Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  Autorenew as RenewIcon,
  DeleteOutline as DeleteIcon,
  PlayCircleOutline as FlowCreatedIcon,
  HighlightOff as FlowDeletedIcon,
  History as HistoryIcon,
  ReceiptLong as InvoiceIcon,
  PendingActions as ApprovalIcon,
} from '@mui/icons-material';

interface ActivityEvent {
  id: string;
  event_type: string;
  source: 'manual' | 'email' | null;
  certificate_type: string | null;
  property_address: string | null;
  flat_label: string | null;
  filename: string | null;
  detail: string | null;
  created_at: string;
}

const EVENT_STYLES: Record<string, { title: string; icon: React.ReactElement; color: string }> = {
  certificate_added: { title: 'Certificate added', icon: <AddIcon />, color: '#2e7d32' },
  certificate_renewed: { title: 'Certificate renewed', icon: <RenewIcon />, color: '#1565c0' },
  certificate_deleted: { title: 'Certificate deleted', icon: <DeleteIcon />, color: '#c62828' },
  flow_created: { title: 'Flow created', icon: <FlowCreatedIcon />, color: '#00897b' },
  flow_deleted: { title: 'Flow deleted', icon: <FlowDeletedIcon />, color: '#ef6c00' },
  invoice_added: { title: 'Invoice added', icon: <InvoiceIcon />, color: '#2e7d32' },
  receipt_added: { title: 'Receipt added', icon: <InvoiceIcon />, color: '#2e7d32' },
  certificate_awaiting_approval: { title: 'Certificate awaiting approval', icon: <ApprovalIcon />, color: '#ef6c00' },
};

const formatWhen = (iso: string) => {
  // Backend timestamps are UTC without a zone suffix
  const date = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const ActivityJournal: React.FC<{ limit?: number }> = ({ limit = 10 }) => {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${config.apiUrl}/api/activity?limit=${limit}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to load activity');
        const data = await response.json();
        setEvents(data.events || []);
      } catch {
        setError('Failed to load recent activity');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [limit]);

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h6">Recent activity</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your last {limit} updates on certificates, invoices, receipts and flows
      </Typography>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && events.length === 0 && <Alert severity="info">No activity yet.</Alert>}

      {events.map(event => {
        const style = EVENT_STYLES[event.event_type] || { title: event.event_type, icon: <HistoryIcon />, color: '#666' };
        const flat = event.flat_label && (/^(flat|apt|apartment|unit)\b/i.test(event.flat_label) ? event.flat_label : `Flat ${event.flat_label}`);
        const where = [event.property_address, flat].filter(Boolean).join(' · ');
        return (
          <Paper key={event.id} variant="outlined" sx={{ display: 'flex', gap: 2, p: 2, mb: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ color: style.color, display: 'flex', pt: 0.25 }}>{style.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>{style.title}</Typography>
                {event.source && (
                  <Chip size="small" variant="outlined" label={event.source === 'email' ? 'via email' : 'manually'} />
                )}
              </Box>
              <Typography variant="body2">
                {[event.certificate_type, where].filter(Boolean).join(' — ') || event.filename || 'Unknown document'}
              </Typography>
              {event.filename && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                  {event.filename}
                </Typography>
              )}
              {event.detail && <Typography variant="caption" color="text.secondary">{event.detail}</Typography>}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {formatWhen(event.created_at)}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

export default ActivityJournal;
